import { loadStripe } from "@stripe/stripe-js";
import { useToast } from "@/composables/useToast.js";
import { authDecrypt } from "@/scripts/actions/encryption";
import SubscriptionService from "@/api/settings/subscription-services";
import { usePlansModal } from "@/features/subscribe/composables/usePlansModal";
const toast = useToast();

let initPromise = null;

const { openPlansModal } = usePlansModal();

export default {
  namespaced: true,
  state: {
    stripe: null,
    plans: [],
    invitations: [],
    subscriptionDetails: null,
  },
  mutations: {
    SET_STRIPE: (state, payload) => (state.stripe = payload),
    SET_PLANS: (state, payload) => (state.plans = payload),
    SET_INVITATIONS: (state, payload) => (state.invitations = payload),
    SET_SUBSCRIPTION_DETAILS: (state, payload) =>
      (state.subscriptionDetails = payload),
  },
  actions: {
    async loadStripe({ commit }) {
      const stripe = await loadStripe(import.meta.env.VITE_STRIPE);
      commit("SET_STRIPE", stripe);
      return stripe;
    },
    async fetchPlans({ commit }, type = null) {
      const plans = await SubscriptionService.getSubscriptionPlans(type);
      commit("SET_PLANS", plans);
      return plans;
    },
    async fetchInvitations({ commit }) {
      const { results: invitations } =
        await SubscriptionService.getPlanInvitations();

      const decryptedInvitations = await Promise.all(
        invitations.map(async (encryptedInvite) => {
          let inviteeEmail = await authDecrypt(encryptedInvite.recipient_email);
          if (inviteeEmail === "FAILED TO DECRYPT") {
            inviteeEmail = encryptedInvite.recipient_email;
          }
          return {
            ...encryptedInvite,
            recipient_email: inviteeEmail,
          };
        })
      );

      commit("SET_INVITATIONS", decryptedInvitations);
      return invitations;
    },
    async sendInvite({ dispatch }, email) {
      await SubscriptionService.invitePlanMember(email);
      await dispatch("fetchInvitations");
    },
    async removeMember({ dispatch }, id) {
      await SubscriptionService.removePlanMember(id);
      await dispatch("fetchInvitations");
    },
    async acceptInvite(context, payload) {
      await SubscriptionService.acceptInvitation(payload);
      await SubscriptionService.getSubscription();
    },
    async cancelSubscription() {
      await SubscriptionService.removeSubscription();
      await SubscriptionService.getSubscription();
      await SubscriptionService.getSubscriptionDetails();
    },
    restoreSubscription({ dispatch }) {
      SubscriptionService.getSubscription();
      dispatch("fetchPlans");
      dispatch("fetchInvitations");
    },
    async cancelSubscriptionRenewal() {
      await SubscriptionService.cancelSubscription();
      await SubscriptionService.getSubscription();
      await SubscriptionService.getSubscriptionDetails();

      setTimeout(() => {
        const url = "https://keepitcloaked.typeform.com/to/UwV968cM";
        window.open(url, "_system");
      }, 2000);
    },
    async fetchSubscriptionDetails({ commit }) {
      let result = null;

      try {
        result = await SubscriptionService.getSubscriptionDetails().catch(
          () => {}
        );
      } finally {
        commit("SET_SUBSCRIPTION_DETAILS", result);
      }

      return result;
    },
    async init({ dispatch }) {
      await dispatch("authentication/waitForAuthentication", null, {
        root: true,
      });

      initPromise =
        initPromise ??
        Promise.all([
          dispatch("loadStripe"),
          dispatch("fetchPlans"),
          dispatch("fetchInvitations"),
        ]);

      return initPromise;
    },
    openSubscriptionModal() {
      openPlansModal();
    },
    awaitSubscriptionChange({ rootGetters }) {
      // backend is not in sync with payment providers, after payment, it takes a few moments for subscription status to update
      return new Promise((resolve) => {
        let pollingInterval = null;
        let count = 0;
        pollingInterval = setInterval(() => {
          const oldProductId =
            rootGetters["settings/getSubscription"]?.product_identifier;

          SubscriptionService.getSubscription()
            .then((newSubscription) => {
              const newProductId = newSubscription?.product_identifier;

              if (
                rootGetters["settings/isSubscribed"] &&
                newProductId !== oldProductId
              ) {
                resolve();
                clearInterval(pollingInterval);
              } else {
                count++;
                if (count === 10) {
                  toast.success(
                    "Still fetching your subscription status, please hold..."
                  );
                } else if (count >= 60) {
                  // after 1 minute, stop polling so this doesnt ping forever
                  throw new Error(
                    "Subscription status not updated after 1 minute"
                  );
                }
              }
            })
            .catch(() => {
              count++;
              if (count >= 60) {
                clearInterval(pollingInterval);
                return toast.error(
                  "There was an error fetching your subscription status. Please try refreshing the page."
                );
              }
            });
        }, 1000);
      });
    },
  },
  getters: {
    getStripe: (state) => state.stripe,
    getPlans: (state) => state.plans,
    getInvitations: (state) => state.invitations,
    getSubscriptionDetails: (state) => state.subscriptionDetails,
  },
};
